import { makeStyles } from '@material-ui/core/styles'
import { COACH_RAIL_CONTAINER_STYLES, COACH_RAIL_SLIDE_STYLES } from '@lib/pga-coach/coach-rail'

const selectHeight = '3.75rem'

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.common.white,
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
  },
  hero: {
    position: 'relative',
    minHeight: 520,
    color: theme.palette.common.white,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      minHeight: 640,
      paddingTop: theme.spacing(6),
      paddingBottom: theme.spacing(6),
    },
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(15, 34, 52, 0.40) 0%, rgba(15, 34, 52, 0.75) 100%)',
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
  },
  heroHeadline: {
    ...theme.typography.h3,
    fontWeight: 800,
    fontSize: '44px',
    lineHeight: 1.28,
    marginBottom: theme.spacing(2),
    maxWidth: 760,
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.down('sm')]: {
      fontSize: '36px',
      lineHeight: theme.typography.h4.lineHeight,
      letterSpacing: theme.typography.h4.letterSpacing,
    },
  },
  heroSummary: {
    ...theme.typography.body1,
    color: theme.palette.common.white,
    maxWidth: 720,
    margin: '0 auto',
    fontSize: '1.125rem',
    lineHeight: 1.6,
    opacity: 0.95,
  },
  searchShell: {
    maxWidth: 980,
    margin: `${theme.spacing(5)}px auto 0`,
    borderRadius: 20,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 16px 48px rgba(15, 34, 52, 0.18)',
  },
  searchField: {
    display: 'flex',
    alignItems: 'stretch',
    backgroundColor: '#eef2f7',
    borderRadius: 14,
    height: selectHeight,
    padding: `0 ${theme.spacing(2.5)}px`,
  },
  searchSelect: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'stretch',
    /**
     * MUI paints a gray focus background on `.MuiSelect-select` only around line-box height;
     * without stretching the input root / killing focus fill, it reads as a “strip” in
     * the taller `#eef2f7` pill.
     */
    '& .MuiInputBase-root': {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'stretch',
    },
    '& .MuiSelect-select': {
      boxSizing: 'border-box',
      overflow: 'hidden',
      width: '100%',
      height: '100%',
      minHeight: selectHeight,
      margin: 0,
      padding: '0 2rem 0 0',
      display: 'flex',
      alignItems: 'center',
      lineHeight: 1.25,
      WebkitTapHighlightColor: 'transparent',
      color: theme.palette.common.black,
      fontSize: '1.125rem',
      fontWeight: 500,
      textTransform: 'none',
      userSelect: 'none',
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSelect-select.Mui-disabled': {
      cursor: 'not-allowed',
    },
    '& .MuiInputBase-root.Mui-focused .MuiSelect-select': {
      backgroundColor: 'transparent',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(21, 47, 76, 0.55)',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
    },
  },
  searchAutocomplete: {
    flex: 1,
    minWidth: 0,
    alignSelf: 'stretch',
    display: 'flex',
    '& .MuiFormControl-root': {
      width: '100%',
      height: '100%',
    },
    '& .MuiAutocomplete-endAdornment': {
      right: theme.spacing(1),
    },
  },
  searchLocationIcon: {
    color: 'rgba(21, 47, 76, 0.55)',
    marginRight: theme.spacing(1.5),
    alignSelf: 'center',
    flexShrink: 0,
  },
  searchAutocompleteInput: {
    height: '100%',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.common.black,
    fontSize: '1.125rem',
    fontWeight: 500,
    '& input': {
      padding: '0 !important',
      height: '100%',
    },
  },
  searchMenuPaper: {
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    border: '1px solid rgba(15, 50, 84, 0.08)',
    boxShadow: '0 6px 20px rgba(15, 34, 52, 0.12), 0 2px 6px rgba(15, 34, 52, 0.06)',
    minWidth: 260,
    marginTop: theme.spacing(1),
    '& ul': {
      padding: 0,
    },
  },
  searchMenuItem: {
    padding: '12px 16px',
    fontSize: theme.typography.pxToRem(15),
    '&:hover:not(.Mui-selected)': {
      backgroundColor: '#f0f2f5',
    },
    '&.Mui-selected': {
      backgroundColor: '#143d66',
      color: theme.palette.common.white,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: '#0f3254',
      },
    },
    '&.MuiAutocomplete-option.Mui-focused': {
      backgroundColor: '#f0f2f5',
    },
    '&.MuiAutocomplete-option.Mui-focused.Mui-selected': {
      backgroundColor: '#0f3254',
      color: theme.palette.common.white,
    },
  },
  searchButton: {
    width: 'auto',
    minWidth: 180,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    minHeight: selectHeight,
    borderRadius: 999,
    fontWeight: 700,
    fontSize: '1.125rem',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  searchFieldCell: {
    [theme.breakpoints.up('md')]: {
      flexBasis: '0 !important',
      flexGrow: 1,
      maxWidth: 'none !important',
    },
  },
  searchButtonCell: {
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      justifyContent: 'flex-end',
      flexBasis: 'auto !important',
      maxWidth: 'none !important',
      width: 'auto',
      paddingLeft: '0 !important',
      paddingRight: '12px !important',
    },
  },
  heroLink: {
    marginTop: theme.spacing(2),
    display: 'inline-flex',
    color: theme.palette.common.white,
    fontWeight: 400,
    textDecoration: 'underline',
    '&:hover': {
      color: theme.palette.common.white,
      textDecoration: 'underline',
    },
  },
  sectionShell: {
    backgroundColor: theme.palette.common.white,
    overflowX: 'hidden',
  },
  section: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
  sectionHeading: {
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  trustHeading: {
    fontSize: '2.5rem',
    color: theme.palette.secondary.main,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2.125rem',
    },
  },
  sectionSummary: {
    color: theme.palette.text.secondary,
    maxWidth: 720,
    margin: '0 auto',
  },
  railHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      textAlign: 'center',
    },
  },
  railHeaderContent: {
    minWidth: 0,
  },
  railHeading: {
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  railSummary: {
    margin: 0,
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      margin: '0 auto',
      textAlign: 'center',
    },
  },
  railCtaDesktop: {
    '& a': {
      padding: 0,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  railCtaMobile: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      marginTop: theme.spacing(3),
      textAlign: 'center',
    },
  },
  railLinkCta: {
    padding: 0,
    height: 35,
  },
  sectionPrimaryCta: {
    minHeight: 52,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    borderRadius: 999,
    fontWeight: 700,
    textTransform: 'none',
  },
  tabsDivider: {
    width: '100%',
    opacity: 0.5,
    height: 1,
    backgroundColor: theme.palette.grey.light,
    marginBottom: theme.spacing(4),
  },
  centeredCta: {
    marginTop: theme.spacing(4),
    textAlign: 'center',
  },
  offeringRail: {
    padding: '8px 0 24px',
    '& .keen-slider__slide': {
      height: 'auto',
    },
  },
  offeringGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      gap: theme.spacing(2.5),
    },
    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(3),
    },
  },
  offeringCard: {
    height: '100%',
    overflow: 'visible !important',
    '& .info-card-with-overlay': {
      height: '100%',
      borderRadius: 8,
      boxShadow: theme.palette.shadow.cardBoxShadow,
      [theme.breakpoints.up('md')]: {
        height: 188,
        flexDirection: 'row',
      },
    },
    '& .info-card-with-overlay--copy-container': {
      [theme.breakpoints.up('md')]: {
        width: 190,
        height: '100%',
        padding: theme.spacing(3),
        paddingRight: 6,
      },
    },
  },
  offeringGridCard: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flex: '0 1 340px',
      maxWidth: 340,
    },
  },
  offeringRailCard: {
    width: '100%',
    [theme.breakpoints.up('xs')]: {
      width: '280px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '320px',
    },
    [theme.breakpoints.up('md')]: {
      width: '340px',
    },
  },
  resultsButton: {
    textTransform: 'none',
  },
  railSection: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(7),
    backgroundColor: theme.palette.grey[100],
    overflowX: 'hidden',
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(5),
    },
  },
  coachRailBody: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(1),
    },
  },
  coachRailLoading: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
  },
  coachRailEmpty: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
  },
  coachRailSliderContainer: {
    ...COACH_RAIL_CONTAINER_STYLES,
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
  },
  coachRailSlider: {
    /**
     * SliderNew applies `padding: 30px 0` to `.keen-slider`; coach rail needs less gap above cards on mobile.
     * `&&` raises specificity so we win over slider-new.styles.js.
     */
    '&&': {
      [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(2),
      },
    },
    '& .keen-slider__slide': COACH_RAIL_SLIDE_STYLES,
  },
  trustPanel: {
    maxWidth: 1120,
    margin: `${theme.spacing(6)}px auto 0`,
    padding: theme.spacing(4),
    borderRadius: 16,
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 18px 40px rgba(15, 34, 52, 0.16)',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(5),
      padding: theme.spacing(3),
    },
  },
  trustSummary: {
    maxWidth: 760,
    color: '#212121',
  },
  trustCard: {
    height: '100%',
    minHeight: 200,
    position: 'relative',
    borderRadius: 10,
    padding: theme.spacing(4, 3),
    backgroundColor: theme.palette.common.white,
    textAlign: 'center',
    boxShadow: '0 18px 40px rgba(15, 34, 52, 0.08)',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 10,
      backgroundColor: '#b59a5b',
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 180,
      padding: theme.spacing(3, 2.5),
    },
  },
  trustIconShell: {
    width: 30,
    height: 30,
    margin: `0 auto ${theme.spacing(2)}px`,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 8px 20px rgba(15, 34, 52, 0.08)',
  },
  trustIcon: {
    fontSize: 14,
    color: '#b59a5b',
  },
  trustCardTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  trustCardDescription: {
    color: '#212121',
    maxWidth: 340,
    margin: '0 auto',
    lineHeight: 1.5,
  },
  resultsRoot: {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(8),
  },
  resultsPanel: {
    padding: theme.spacing(3),
    borderRadius: 20,
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 16px 40px rgba(15, 34, 52, 0.08)',
  },
  filterRow: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  chipFilter: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  statLine: {
    color: theme.palette.text.secondary,
  },
  searchHeaderMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  saveButton: {
    minHeight: 56,
    borderRadius: 14,
  },
  resultCard: {
    height: '100%',
    borderRadius: 18,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 12px 32px rgba(15, 34, 52, 0.08)',
  },
  resultCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  resultCardTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  resultCardMeta: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  resultCardActions: {
    marginTop: 'auto',
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8, 3),
  },
  inlineStack: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  guidesSectionShell: {
    backgroundColor: theme.palette.common.white,
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
    scrollMarginTop: theme.spacing(12),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
    },
  },
  guidesArticlesHost: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'hidden',
  },
  guidesSectionContent: {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    maxWidth: '100%',
    minWidth: 0,
  },
  citySection: {
    textAlign: 'center',
    paddingTop: 0,
    paddingBottom: 0,
  },
  citySectionShell: {
    backgroundColor: theme.palette.grey[100],
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    overflowX: 'hidden',
  },
  citySectionHeadline: {
    marginBottom: theme.spacing(2),
  },
  citySectionSummary: {
    color: '#212121',
    marginBottom: theme.spacing(4),
  },
  cityPills: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(2),
    maxWidth: 760,
    margin: `0 auto ${theme.spacing(4)}px`,
  },
  cityPill: {
    /**
     * Soft destination pill: white surface on the gray field with a
     * subtle drop shadow, body-weight (600) sentence-case label in
     * the default `text.primary` ink. Visually paired with the
     * flagged-profile skill chips — same surface
     * treatment (white pill, soft shadow, 999 radius, sentence case,
     * weight 600, body font) so the two read as one design language.
     * Diverges only on size: city pills are tappable destinations
     * (56px tall, 1.125rem) while chips are compact taxonomy tags
     * (30px tall, 14px) on the coach profile.
     */
    minHeight: 56,
    padding: theme.spacing(1.75, 3),
    borderRadius: 999,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    fontSize: '1.125rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 10px 28px rgba(15, 34, 52, 0.08)',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      boxShadow: '0 12px 32px rgba(15, 34, 52, 0.1)',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: 320,
    },
  },
  citySectionFooter: {
    marginTop: theme.spacing(4),
  },
}))

export default useStyles
